// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { carouselTypeOptions } from "@rc/Overrides/Widgets/Common/ProductCarousel";
import { XngHasProductContext } from "@rc/Overrides/Widgets/ProductList/ProductListProductContext";
import { color } from "@rc/XDL/Utilities";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;

    // xng-change
    showCompare?: boolean;
    showAddToList?: boolean;
    showPrice?: boolean;
    showImage?: boolean;
}
const mapStateToProps = (state: CustomAppState, props: XngHasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
    recentlyViewedStoreParentProducts: state.xRecentlyViewedCarousel.recentlyViewedStoreParentProducts,
    currentPage: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    XngHasProductContext;

export interface ProductListProductInformationStyles {
    availabilityStyles?: ProductAvailabilityStyles;
    attributes?: ProductAttributesStyles;

    // xng-change
    // grid
    gridProductNameWrapper?: InjectableCss;
    gridProductDescriptionStyles?: ProductDescriptionStyles;

    // list
    listInformationWrapper?: InjectableCss;
    listProductNameWrapper?: InjectableCss;
    listProductNumbersWrapper?: InjectableCss;
    listProductDescriptionStyles?: ProductDescriptionStyles;
    listProductAvailabilityWrapper?: InjectableCss;

    // shared
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    compareCheckbox?: CheckboxPresentationProps;
    addToListWrapper?: InjectableCss;
    fullWidthItem?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    actions?: InjectableCss;
    price?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;

    inlineRating?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    gridProductNameWrapper: {
        css: css`
            min-height: 46px;
            margin-bottom: 4px;
        `,
    },
    gridProductDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    &&&& {
                        width: 100%;
                        font-size: 16px;
                        line-height: 17px;
                        color: ${color("common.ui6")};
                        ${wrapInContainerStyles}
                    }
                `,
            },
        },
        productDetailDescription: {
            css: css`
                &&&& {
                    font-size: 16px;
                    line-height: 17px;
                    color: ${color("common.ui6")};
                }
            `,
        },
    },
    listInformationWrapper: {
        css: css`
            padding-top: 0;
            padding-bottom: 20px;
            flex-grow: 0;

            &.grid-information-wrapper {
                padding-bottom: 0;
                display: flex;
                flex-direction: column;
            }
        `,
    },
    listProductNameWrapper: {
        css: css`
            max-height: 56px;
            overflow: hidden;
            min-height: 56px;
            margin-bottom: 2px;
            position: relative;
        `,
    },
    listProductNumbersWrapper: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    listProductDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    &&&& {
                        width: 100%;
                        font-size: 18px;
                        line-height: 25px;
                        color: ${color("common.ui6")};
                        ${wrapInContainerStyles}
                    }
                `,
            },
        },
        productDetailDescription: {
            css: css`
                &&&& {
                    font-size: 18px;
                    line-height: 25px;
                    color: ${color("common.ui6")};
                }
            `,
        },
    },
    compareCheckbox: {
        css: css`
            align-self: center;
            padding-top: 15px;
            margin-left: 0;
        `,
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    fullWidthItem: {
        css: css`
            width: 100%;
        `,
    },
    actions: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            max-width: 250px;
        `,
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    inlineRating: {
        css: css`
            width: 100%;
            height: 22px;
            margin-bottom: 6px;

            .bv_main_container svg {
                max-height: 14px !important;
                max-width: 14px !important;
            }

            .bv_main_container .bv_numReviews_component_container {
                font-size: 14px !important;
                font-family: "DINPro-Medium" !important;
            }
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 260px;
            min-width: 260px;
            height: 260px;
            margin: 0 auto;
            margin-bottom: 16px;
        `,
    },
    priceWrapper: {
        css: css`
            margin-top: auto;
        `
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,

    // xng-change
    showCompare,
    showAddToList,
    showPrice,
    showImage,
    isProductCompareFull,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    view,
    recentlyViewedStoreParentProducts,
    currentPage,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const toasterContext = useContext(ToasterContext);
    const carouselType = productContext?.carouselType;

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <StyledWrapper>
            <StyledWrapper
                {...styles.listInformationWrapper}
                className={`${view === "Grid" || (carouselTypeOptions.some(item => item.value === carouselType) && recentlyViewedStoreParentProducts?.length > 0) ? 'grid-information-wrapper' : ''}`}
            >
                {/* RC doesn't have brands */}
                {/* {product.brand && showBrand && (
                <StyledWrapper {...styles.brandItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </StyledWrapper>
            )} */}
                {
                    (view === 'Grid' || carouselTypeOptions.some(item => item.value === carouselType) || currentPage === 'ProductComparePage') && (
                        <StyledWrapper {...styles.imageWrapper}>
                            {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                            <ProductListProductImage
                                showImage={showImage || false}
                                showCompare={false}
                                showImageBadge={showImageBadges}
                                showTextBadge={showTextBadges}
                            />
                        </StyledWrapper>
                    )
                }
                {showTitle && view === "List" && (
                    <StyledWrapper {...styles.listProductNameWrapper}>
                        <ProductDescription product={productContext} extendedStyles={styles.listProductDescriptionStyles} />
                    </StyledWrapper>
                )}
                {showTitle && view !== "List" && (
                    <StyledWrapper {...styles.gridProductNameWrapper}>
                        <ProductDescription product={productContext} extendedStyles={styles.gridProductDescriptionStyles} {... { truncate: true }} />
                    </StyledWrapper>
                )}
                {showPartNumbers && (
                    <StyledWrapper {...styles.listProductNumbersWrapper}>
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.inlineRating}>
                    {product.properties?.["wwMainItemCode"]?.length > 0 && (
                        <div data-bv-show="inline_rating" data-bv-product-id={product.properties["wwMainItemCode"]}></div>
                    )}
                </StyledWrapper>
                {variantDisplayTrait && (
                    <StyledWrapper {...styles.fullWidthItem}>
                        <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                    </StyledWrapper>
                )}
                {showTextBadges && textBadges && (
                    <StyledWrapper {...styles.fullWidthItem}>
                        <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                    </StyledWrapper>
                )}
                {showImageBadges && imageBadges && (
                    <StyledWrapper {...styles.fullWidthItem}>
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                    </StyledWrapper>
                )}
                {showAvailability && (
                    <StyledWrapper {...styles.listProductAvailabilityWrapper}>
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    </StyledWrapper>
                )}
                {showAttributes && (
                    <StyledWrapper {...styles.fullWidthItem}>
                        <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.actions}>
                    {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                        <Checkbox
                            {...styles.compareCheckbox}
                            checked={productCompareChecked}
                            onChange={handleChanged}
                            data-test-selector="productList_CompareCheckbox"
                        >
                            {translate("Compare")}
                        </Checkbox>
                    )}
                    {showAddToList && (
                        <StyledWrapper {...styles.addToListWrapper}>
                            <ProductAddToListLink
                                data-test-selector={`actionsAddToList${productContext.product.id}`}
                                extendedStyles={styles.addToListLink}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductInformation));
